
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Instagram, Facebook, Twitter, Youtube, Phone } from 'lucide-react';
import { siteConfig } from '../config/site';
import { useLanguage } from '../App';

interface SlideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SlideMenu: React.FC<SlideMenuProps> = ({ isOpen, onClose }) => {
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: t.brandStoryTitle, path: '/brand' },
    { label: 'Gallery', path: '/gallery' },
    { label: t.signatureHouseCall, path: '/#services' },
    { label: t.availability, path: '/#coverage' },
    { label: t.clientVoice, path: '/#reviews' },
    { label: t.bookTheCall, path: siteConfig.bookingUrl, external: true },
  ];

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/70 backdrop-blur-md z-[60] transition-opacity duration-700 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div 
        className={`fixed top-0 left-0 h-full w-full sm:w-[450px] bg-primary z-[70] transition-transform duration-700 cubic-bezier(0.77, 0, 0.175, 1) border-r border-accent/10 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-8 sm:p-12 flex items-start justify-between">
          <div className="flex flex-col gap-6">
            <Link to="/" onClick={onClose} className="group">
              <img src={siteConfig.images.logo} alt="Santi Bladez Logo" className="w-20 h-20 object-contain rounded-full border-2 border-accent/20 group-hover:scale-110 transition-transform duration-500 shadow-2xl bg-black/40" />
            </Link>
            <div className="flex gap-5">
              <a href={siteConfig.socials.instagram} target="_blank" rel="noopener" className="text-accent/40 hover:text-accent transition-colors"><Instagram size={20} /></a>
              <a href={siteConfig.socials.facebook} target="_blank" rel="noopener" className="text-accent/40 hover:text-accent transition-colors"><Facebook size={20} /></a>
              <a href={siteConfig.socials.twitter} target="_blank" rel="noopener" className="text-accent/40 hover:text-accent transition-colors"><Twitter size={20} /></a>
              <a href={siteConfig.socials.youtube} target="_blank" rel="noopener" className="text-accent/40 hover:text-accent transition-colors"><Youtube size={20} /></a>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-accent hover:text-white transition-colors hover:rotate-90 duration-300" aria-label="Close Menu">
            <X size={36} />
          </button>
        </div>

        <nav className="flex-1 px-8 sm:px-12 py-4 overflow-y-auto scrollbar-hide">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.label}>
                {item.external ? (
                  <a 
                    href={item.path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-3xl sm:text-4xl font-serif font-bold text-white hover:text-accent transition-all duration-500 block py-3 border-b border-white/5 uppercase tracking-wide group"
                  >
                    <span className="inline-block group-hover:translate-x-2 transition-transform">{item.label}</span>
                  </a>
                ) : item.path.startsWith('/#') ? (
                  <Link 
                    to={item.path} 
                    onClick={onClose}
                    className="text-3xl sm:text-4xl font-serif font-bold text-white hover:text-accent transition-all duration-500 block py-3 border-b border-white/5 uppercase tracking-wide group"
                  >
                    <span className="inline-block group-hover:translate-x-2 transition-transform">{item.label}</span>
                  </Link>
                ) : (
                  <Link 
                    to={item.path} 
                    onClick={onClose}
                    className="text-3xl sm:text-4xl font-serif font-bold text-white hover:text-accent transition-all duration-500 block py-3 border-b border-white/5 uppercase tracking-wide group"
                  >
                    <span className="inline-block group-hover:translate-x-2 transition-transform">{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-8 sm:p-12 border-t border-white/5 space-y-8 bg-secondary/20">
          <div className="flex items-center gap-6 text-sm">
            <button 
              onClick={() => setLang('en')}
              className={`${lang === 'en' ? 'text-accent' : 'text-white/40'} hover:text-accent transition-colors font-black tracking-widest uppercase`}
            >
              English
            </button>
            <span className="text-white/10">|</span>
            <button 
              onClick={() => setLang('es')}
              className={`${lang === 'es' ? 'text-accent' : 'text-white/40'} hover:text-accent transition-colors font-medium tracking-widest uppercase`}
            >
              Español
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={`tel:${siteConfig.phone}`} className="flex-1 flex items-center justify-center gap-3 py-4 bg-secondary border border-accent/20 text-[10px] font-black uppercase tracking-[0.3em] hover:bg-accent hover:text-black hover:border-accent transition-all duration-500">
              <Phone size={14} /> Contact
            </a>
            <a href={siteConfig.bookingUrl} target="_blank" rel="noopener" className="flex-1 flex items-center justify-center gap-3 py-4 bg-accent text-black border border-accent text-[10px] font-black uppercase tracking-[0.3em] hover:bg-black hover:text-accent transition-all duration-500 shadow-xl">
              {t.bookNow}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlideMenu;
